import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';

import { AuthApi } from '../../../features/auth/data-access/auth.api';
import {
  AuthTokensDto,
  ObtainTokensRequestDto,
} from '../../../features/auth/data-access/auth.dto';
import { TokenService } from '../token/token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private readonly authApi = inject(AuthApi);
  private readonly tokenService = inject(TokenService);
  private refreshRequest$: Observable<AuthTokensDto> | null = null;

  isAuthenticated(): boolean {
    return this.tokenService.hasAccessToken() || this.tokenService.hasRefreshToken();
  }

  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenService.getRefreshToken();
  }

  clear(): void {
    this.refreshRequest$ = null;
    this.tokenService.clearTokens();
  }

  login(payload: ObtainTokensRequestDto): Observable<AuthTokensDto> {
    return this.authApi.login(payload).pipe(
      tap((tokens) => this.tokenService.setTokens(tokens)),
    );
  }

  refreshTokens(): Observable<AuthTokensDto> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('Refresh token is missing.'));
    }

    if (!this.refreshRequest$) {
      this.refreshRequest$ = this.authApi.refreshTokens(refreshToken).pipe(
        tap((tokens) => this.tokenService.setTokens(tokens)),
        catchError((error) => {
          this.clear();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );
    }

    return this.refreshRequest$;
  }

  shouldRefreshAccessToken(): boolean {
    return this.tokenService.isAccessTokenExpiringSoon();
  }
}
